/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useCallback } from 'react';
import { VarName } from '../../../services/api';
import { getMotionMap, MarkerData } from '../mapHelpers';
import { SensorValueItem } from '../../CalendarView/helpers';
import { setClickedItem, setHighlightedItem } from '../../../state/actions';
import { ActiveItem } from '../../../state/types';
import getFloorplanLayer from './floorPlanLayer';
import { DeckGLViewPort } from '../types';
import getLabelLayer from './labelLayer';
import getTargetPosLayer from './targetPosLayer';
import getSummaryCoverageLayer from './summaryCoverageLayer';
import getSensorCoverageLayer from './sensorCoverageLayer';
import { useAppDispatch, useAppSelector } from '../../../state/store';
import {
  getActiveMarker,
  getClickedItem,
  getCurrentLocation,
  getCurrentLocationFloorplan,
  getHighlightedItem,
  getMotionThreshold,
  getUserLocation,
  getUserPosition,
} from '../../../state/selectors';
import getOccSignaturesLayer from './occSignaturesLayer';
import getMotionPins from './motionPins';

export default function useFloorLayers(
  viewState: DeckGLViewPort,
  markerData: MarkerData[],
  overrideData?: SensorValueItem[]
) {
  const dispatch = useAppDispatch();
  const currentLocation = useAppSelector(getCurrentLocation);
  const highlightedItem = useAppSelector(getHighlightedItem);
  const clickedItem = useAppSelector(getClickedItem);
  const motionThreshold = useAppSelector(getMotionThreshold);
  const userPosition = useAppSelector(getUserPosition);
  const userLocation = useAppSelector(getUserLocation);
  const activeMarker = useAppSelector(getActiveMarker);
  const floorPlan = useAppSelector(getCurrentLocationFloorplan);

  const deckGLLayers: any[] = [];

  const motionMap = useMemo(() => {
    const isUtlData = activeMarker === VarName.MotionEvent && overrideData !== undefined;
    return getMotionMap(markerData, isUtlData);
  }, [activeMarker, overrideData, markerData]);

  const onSensorHover = useCallback(
    (item: ActiveItem) => {
      dispatch(setHighlightedItem(item));
    },
    [dispatch]
  );

  const onSensorClick = useCallback(
    (item: ActiveItem) => {
      dispatch(setClickedItem(item));
    },
    [dispatch]
  );

  const showMotion = activeMarker === VarName.MotionEvent;
  const showOverrideData = overrideData !== undefined;

  const floorPlanLayer = useMemo(() => {
    if (!floorPlan) return null;

    return getFloorplanLayer(
      floorPlan,
      viewState.zoom,
      showMotion,
      highlightedItem.id ? highlightedItem : clickedItem,
      motionMap,
      motionThreshold,
      showOverrideData,
      onSensorHover,
      onSensorClick
    );
  }, [
    floorPlan,
    viewState.zoom,
    showMotion,
    highlightedItem,
    clickedItem,
    motionMap,
    motionThreshold,
    showOverrideData,
    onSensorHover,
    onSensorClick,
  ]);

  // Used to add labels to the floorplan
  const labelLayer = useMemo(() => {
    if (!floorPlan) return null;
    return getLabelLayer(floorPlan, viewState.zoom);
  }, [floorPlan, viewState.zoom]);

  // Used to plot the users location on the map
  const targetPosLayer = useMemo(() => {
    if (!userPosition || userLocation !== currentLocation) return null;
    return getTargetPosLayer(userPosition);
  }, [userPosition, userLocation, currentLocation]);

  // Used to plot a circle around the user location on the map
  const perimeterPolygon = useMemo(() => {
    if (!userPosition || userLocation !== currentLocation) return null;
    return getSummaryCoverageLayer(userPosition);
  }, [userPosition, userLocation, currentLocation]);

  // Used to plot coloured area around sensors indicating coverage
  const sensorCoverageLayer = useMemo(() => {
    if (!floorPlan) return null;
    return getSensorCoverageLayer(activeMarker, markerData, floorPlan);
  }, [activeMarker, markerData, floorPlan]);

  // Used for data with occupancy signatures to plot on Map measured positions of people
  const occPinLayer = useMemo(() => {
    if (activeMarker !== VarName.OccSignatures) return null;
    return getOccSignaturesLayer(markerData, dispatch);
  }, [activeMarker, dispatch, markerData]);

  const motionPinLayer = useMemo(() => {
    if (activeMarker !== VarName.MotionEvent) return null;
    const isUtlData = overrideData !== undefined;
    return getMotionPins(markerData, isUtlData);
  }, [activeMarker, markerData, overrideData]);

  if (labelLayer !== null) {
    deckGLLayers.push(labelLayer);
  }

  if (sensorCoverageLayer !== null) {
    deckGLLayers.push(sensorCoverageLayer);
  }

  if (occPinLayer !== null) {
    deckGLLayers.push(occPinLayer);
  }

  if (motionPinLayer !== null) {
    deckGLLayers.push(motionPinLayer);
  }

  if (targetPosLayer !== null) {
    deckGLLayers.push(targetPosLayer);
  }

  if (perimeterPolygon !== null) {
    deckGLLayers.push(perimeterPolygon);
  }

  if (floorPlanLayer !== null) {
    deckGLLayers.push(floorPlanLayer);
  }

  return deckGLLayers;
}
